
import React, { useState, useEffect } from 'react';
import { User, UserRole, SystemSettings } from '../types';
import { Button, Input, Card } from './ui';
import { api } from '../services/api';
import { Save, User as UserIcon, Building, Upload, Image as ImageIcon, LayoutTemplate, Phone } from 'lucide-react';

interface SettingsProps {
  user: User;
  onUpdateUser: (u: User) => void;
}

export const Settings = ({ user, onUpdateUser }: SettingsProps) => {
  const [name, setName] = useState(user.name);
  const [clinicName, setClinicName] = useState(user.clinicName || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Admin Settings
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  useEffect(() => {
    if (user.role === UserRole.ADMIN) {
       api.getSettings().then(setSystemSettings);
    }
  }, [user]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    
    try {
      await api.updateProfile(user.id, { name, clinicName, phone });
      onUpdateUser({ ...user, name, clinicName, phone });
      setMessage('Profile updated successfully!');
    } catch (err: any) {
      console.error(err);
      setMessage('Failed to update profile: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const handleSaveAdminSettings = async () => {
     if (!systemSettings) return;
     setLoading(true);
     try {
        let logoUrl = systemSettings.logoUrl;
        
        if (logoFile) {
           // We use the existing uploadFile function which uploads to 'order-files' bucket.
           // Since orderId is null, it just uploads the file and returns the URL.
           // Ideally, we'd have a 'branding' bucket, but we reuse existing resources to minimize config.
           logoUrl = await api.uploadFile(null, logoFile); 
        }

        await api.updateSettings({ ...systemSettings, logoUrl });
        setMessage('System settings updated! Refresh to see logo changes.');
        setLogoFile(null); // Clear pending file
     } catch (err: any) {
        console.error(err);
        setMessage('Failed to update system settings: ' + (err.message || 'Check database permissions'));
     } finally {
        setLoading(false);
        setTimeout(() => setMessage(''), 3000);
     }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-fade-in pb-20">
       <div className="mb-6">
         <h1 className="text-3xl font-bold text-zinc-900 mb-2">Account Settings</h1>
         <p className="text-zinc-500">Manage your profile details and preferences.</p>
       </div>

       <Card>
         <div className="flex items-center gap-6 mb-8 border-b border-zinc-100 pb-6">
            <div className="w-20 h-20 bg-zinc-100 rounded-full flex items-center justify-center text-3xl font-bold text-zinc-600 border border-zinc-200">
               {user.name.charAt(0)}
            </div>
            <div>
               <h2 className="text-2xl font-bold text-zinc-900 mb-1">{user.name}</h2>
               <div className="flex gap-2">
                  <span className="text-xs px-2.5 py-1 rounded bg-blue-50 text-blue-600 border border-blue-100 font-bold uppercase tracking-wide">{user.role}</span>
                  <span className="text-xs px-2.5 py-1 rounded bg-zinc-50 text-zinc-500 border border-zinc-100 font-medium">{user.email}</span>
               </div>
            </div>
         </div>

         <form onSubmit={handleSaveProfile} className="space-y-6">
            <div className="grid gap-6">
               <div className="relative">
                  <Input 
                    label="Full Name" 
                    value={name} 
                    onChange={(e: any) => setName(e.target.value)} 
                  />
                  <UserIcon className="absolute right-4 top-[38px] text-zinc-400" size={18} />
               </div>

               <div className="relative">
                  <Input 
                    label="WhatsApp Number" 
                    value={phone} 
                    onChange={(e: any) => setPhone(e.target.value)} 
                    placeholder="+20 1xxxxxxxxx"
                  />
                  <Phone className="absolute right-4 top-[38px] text-zinc-400" size={18} />
               </div>

               {user.role === UserRole.DENTIST && (
                  <div className="relative">
                     <Input 
                       label="Clinic Name" 
                       value={clinicName} 
                       onChange={(e: any) => setClinicName(e.target.value)} 
                     />
                     <Building className="absolute right-4 top-[38px] text-zinc-400" size={18} />
                  </div>
               )}
            </div>
            
            <div className="pt-4 flex items-center gap-4">
               <Button type="submit" disabled={loading}>
                  <Save size={18} />
                  {loading ? 'Saving...' : 'Save Profile'}
               </Button>
               {message && (
                  <span className={`text-sm ${message.includes('Failed') ? 'text-red-500' : 'text-green-600'}`}>
                     {message}
                  </span>
               )}
            </div>
         </form>
       </Card>

       {user.role === UserRole.ADMIN && systemSettings && (
          <div className="space-y-6 animate-slide-up">
             <h2 className="text-2xl font-bold text-zinc-900 mt-12 mb-4 flex items-center gap-2">
                <LayoutTemplate size={24}/> System Branding
             </h2>
             
             <Card className="border-2 border-blue-500/10 shadow-lg relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                   <ImageIcon size={120} />
                </div>

                <div className="mb-6">
                   <h3 className="text-lg font-bold text-zinc-900">Custom Logo</h3>
                   <p className="text-sm text-zinc-500">Upload your lab's logo to replace the default icon across the app.</p>
                </div>
                
                <div className="space-y-6">
                   <div className="flex flex-col md:flex-row items-start md:items-center gap-8 p-6 bg-zinc-50 rounded-2xl border border-dashed border-zinc-300">
                      
                      {/* Preview Area */}
                      <div className="flex flex-col items-center gap-2">
                         <span className="text-xs font-bold text-zinc-400 uppercase">Preview</span>
                         <div className="w-32 h-32 bg-white rounded-xl flex items-center justify-center border border-zinc-200 shadow-sm relative overflow-hidden group">
                            {logoFile ? (
                               <img src={URL.createObjectURL(logoFile)} className="w-full h-full object-contain p-4" alt="Preview" />
                            ) : systemSettings.logoUrl ? (
                               <img src={systemSettings.logoUrl} className="w-full h-full object-contain p-4" alt="Current Logo" />
                            ) : (
                               <div className="text-zinc-300 flex flex-col items-center">
                                  <ImageIcon size={32} className="mb-2" />
                                  <span className="text-xs">No Logo</span>
                               </div>
                            )}
                         </div>
                      </div>

                      {/* Upload Controls */}
                      <div className="flex-1 w-full">
                         <label className="block text-sm font-bold text-zinc-700 mb-2">Select Image File</label>
                         <div className="flex gap-3">
                            <input 
                              type="file" 
                              id="logo-upload" 
                              accept="image/png,image/jpeg,image/svg+xml" 
                              className="hidden" 
                              onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                            />
                            <label 
                              htmlFor="logo-upload" 
                              className="cursor-pointer flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-white border border-zinc-300 rounded-xl text-sm font-bold text-zinc-700 hover:bg-zinc-50 hover:border-zinc-400 shadow-sm transition-all"
                            >
                               <Upload size={18}/> {logoFile ? 'Change File' : 'Choose File'}
                            </label>
                         </div>
                         <p className="text-xs text-zinc-500 mt-3">
                            Supported: PNG, JPG, SVG. Max size: 2MB. <br/>
                            Recommended: Transparent PNG, at least 200x200px.
                         </p>
                      </div>
                   </div>

                   <div className="flex items-center justify-end gap-4 pt-2">
                      {logoFile && (
                         <span className="text-sm text-blue-600 font-medium animate-pulse">
                            Ready to upload: {logoFile.name}
                         </span>
                      )}
                      <Button onClick={handleSaveAdminSettings} disabled={loading} className="px-8">
                         <Save size={18} />
                         {loading ? 'Uploading...' : 'Save & Apply'}
                      </Button>
                   </div>
                </div>
             </Card>
          </div>
       )}
    </div>
  );
};
